import { ScopeWasteType } from 'models/ActivityDataEntryScopes/scopeThree.model';
import {
  ScopeData,
  ScopeMonthlyData,
} from 'models/ActivityDataEntryScopes/scopes.model';
import { OrganizationMetaData } from 'models/OrganizationMetaData.ts';
import { roundingTheFloatNumber } from 'shared/utils/roundingTheFloatNumber';
import { monthListWithFullName } from 'views/CompanySetup/companySetupConstants';
import { wasteTreatmentOptions } from './wasteTreatementOptions';

export const emptyCalendarMonthlySlots = (): ScopeMonthlyData[] =>
  monthListWithFullName.map(month => ({
    month: Number(month.value),
    value: undefined,
  }));

export const toCalendarMonthlySlots = (
  data?: ScopeMonthlyData[] | null
): ScopeMonthlyData[] => {
  const slots = emptyCalendarMonthlySlots();
  data?.forEach(item => {
    const month = Number(item?.month);
    if (month >= 1 && month <= 12) {
      slots[month - 1] = {
        month,
        value: item?.value == null ? undefined : Number(item.value),
      };
    }
  });
  return slots;
};

export const sumMonthlyValues = (data?: ScopeMonthlyData[] | null): number =>
  data?.reduce((acc, item) => acc + (item?.value ? Number(item.value) : 0), 0) ??
  0;

export const formatMonthlyTotal = (totalValue: number): number | string =>
  Number.isInteger(totalValue)
    ? totalValue
    : totalValue.toFixed(4).replace(/\.?0+$/, '');

export const hasUniqueCalendarMonths = (
  data?: ScopeMonthlyData[] | null
): boolean => {
  if (!data?.length) return true;
  const months = data
    .map(item => Number(item?.month))
    .filter(month => !Number.isNaN(month));
  return new Set(months).size === months.length;
};

export const hasValidCalendarMonths = (
  data?: ScopeMonthlyData[] | null
): boolean => {
  if (!data?.length) return false;
  return data.every(item => {
    const month = Number(item?.month);
    return month >= 1 && month <= 12;
  });
};

export const monthlySumEqualsTotal = (
  data?: ScopeMonthlyData[] | null,
  total?: number | string | null
): boolean => {
  const monthlySum = roundingTheFloatNumber(sumMonthlyValues(data), 4);
  const roundedTotal = roundingTheFloatNumber(Number(total) || 0, 4);
  return monthlySum === roundedTotal;
};

export const isYearlyWasteRow = (row?: ScopeWasteType): boolean =>
  row?.isYearly !== false;

export const mapWasteRowToScopeData = (
  row?: ScopeWasteType,
  wasteTypeListMeta?: OrganizationMetaData[],
  clearMonthly?: boolean
): ScopeData => {
  const wasteLabel =
    wasteTypeListMeta?.find(item => item.value === row?.wasteTypeId)?.label ??
    '';
  const treatmentLabel =
    wasteTreatmentOptions.find(item => item.value === row?.treatmentMethod)
      ?.label ?? '';
  const title = [wasteLabel, treatmentLabel].filter(Boolean).join(' - ');
  return Object.assign(new ScopeData(), {
    id: row?.id,
    fuelName: title || 'Waste',
    fuelValue: row?.totalWasteProduced,
    isYearly: isYearlyWasteRow(row),
    scopeMonthlyData: clearMonthly
      ? undefined
      : row?.scopeMonthlyData
      ? toCalendarMonthlySlots(row.scopeMonthlyData)
      : undefined,
  });
};

export const normalizeWasteRowForSubmit = (
  row: ScopeWasteType
): ScopeWasteType => {
  if (isYearlyWasteRow(row)) {
    return {
      ...row,
      isYearly: true,
      scopeMonthlyData: null,
    };
  }
  return {
    ...row,
    isYearly: false,
    scopeMonthlyData: toCalendarMonthlySlots(row.scopeMonthlyData),
  };
};

export const withDefaultYearlyFlag = (
  rows?: ScopeWasteType[]
): ScopeWasteType[] => {
  const source = rows?.length ? rows : [new ScopeWasteType()];
  return source.map(row => ({
    ...row,
    isYearly: isYearlyWasteRow(row),
  }));
};
